import { skipToken } from '@apollo/client/react'
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'
import { openShareArtworkDialog } from 'src/components/ArtworkDetail/ArtworkShare/ArtworkShareDialog'
import { PublishTaskForm } from 'src/components/GenerationTask/PublishTaskButton'
import { Preloader } from 'src/components/Preloader/Preloader'
import type { ArtworkInfoFormValue } from 'src/components/SubmitArtwork/ArtworkInfoForm'
import { type ArtworkInfoFormProps } from 'src/components/SubmitArtwork/ArtworkInfoForm'
import {
  UIDialog,
  UIDialogResponsive,
  type UIModalProps,
} from 'src/components/ui/Modal'
import type { Artwork, Task } from 'src/services/apollo/generated/graphql'
import { useTaskById } from 'src/services/apollo/task'

export interface TaskPublishDialogCommonProps {
  afterPublish?: (artwork: Artwork) => void
  scene?: string
  formInit?: Partial<ArtworkInfoFormValue>
  formProps?: Omit<ArtworkInfoFormProps<ArtworkInfoFormValue>, 'form'>
}

export interface TaskPublishDialogContentProps extends TaskPublishDialogCommonProps {
  input?: Task | string
  mediaIdx?: number
  close: () => void
}

export interface TaskPublishDialogProps extends TaskPublishDialogCommonProps {
  onClose?: () => void
  dialogProps?: Omit<UIModalProps, 'open' | 'onClose'>
}

export type TaskPublishDialogRef = {
  open: (task: Task | string, mediaIdx?: number) => void
  close: () => void
}

function TaskPublishDialogContent(props: TaskPublishDialogContentProps) {
  const { input, mediaIdx } = props
  const { data = typeof input === 'string' ? undefined : input } = useTaskById(
    typeof input === 'string' ? input : skipToken,
  )

  const afterPublish = useCallback(
    (artwork: Artwork) => {
      props.afterPublish?.(artwork)
      openShareArtworkDialog(artwork, {
        trackingArgs: { scene: props.scene },
      })
    },
    [props.scene, props.afterPublish],
  )

  return data ? (
    <PublishTaskForm
      task={data}
      mediaIdx={mediaIdx}
      afterPublish={afterPublish}
      scene={props.scene}
      onSuccess={props.close}
      initialValues={props.formInit}
      formProps={props.formProps}
    />
  ) : (
    <Preloader />
  )
}

export default forwardRef<TaskPublishDialogRef, TaskPublishDialogProps>(
  function TaskPublishDialog(props, ref) {
    const [input, setInput] = useState<Task | string>()
    const [mediaIdx, setMediaIdx] = useState<number>()

    function close() {
      props.onClose?.()
      setInput(undefined)
      setMediaIdx(undefined)
    }

    useImperativeHandle(
      ref,
      () => ({
        open: (task, mediaIdx) => {
          setInput(task)
          setMediaIdx(mediaIdx)
        },
        close,
      }),
      [],
    )

    return (
      <UIDialogResponsive
        open={Boolean(input)}
        onClose={close}
        dismissable
        className="w-[400px] max-w-screen-msm"
        {...props.dialogProps}
      >
        <TaskPublishDialogContent
          close={close}
          input={input}
          mediaIdx={mediaIdx}
          afterPublish={props.afterPublish}
          scene={props.scene}
          formInit={props.formInit}
          formProps={props.formProps}
        />
      </UIDialogResponsive>
    )
  },
)

export async function openTaskPublishDialog(
  args?: Omit<TaskPublishDialogContentProps, 'close'>,
) {
  return UIDialog.create({
    responsive: true,
    dismissable: true,
    className: 'w-[600px] max-w-[calc(100vw-32px)]',
    children: ({ close }) => (
      <TaskPublishDialogContent {...args} close={close} />
    ),
  })
}
